/*
 * GE GOLD -- what the child, the parent and the owner are all told about work
 * that is BEHIND the student, composed in ONE place.
 *
 * Two opposite states share a screen and must never be confused
 * (../wicked-maths-laravel/docs/PROGRESSION_LOCKDOWN.md §1):
 *
 *   grey  -- "not open YET". Ahead of them, locked, cannot be entered.
 *   gold  -- signed off by the owner. Behind them, VISIBLE, openable and
 *            practisable -- it simply earns nothing.
 *
 * ## GOLD IS CONFERRED, NEVER EARNED
 *
 * The owner, 17 Sep 2026, settling it: "No, GOLD can only be conferred by me -
 * even if the app says a student has 100%, it remains purple until I 'close' it
 * - GOLD is for closed as that is final and some students will be made to sit
 * exams to prove their 100% before getting their gold."
 *
 * So **the client never derives gold from a percentage, anywhere**. Not on a
 * tile, not on a course card, not on the overall ring. A section at 100% that
 * she has not closed is purple, exactly as it was before any of this existed.
 * Finishing the questions is not the achievement; her sign-off is, and for some
 * children it arrives only after an exam.
 *
 * ## TWO flags
 *
 * The server sends both on modules, lesson groups and lessons:
 *
 *   `gold`          -- the CELEBRATION. Her sign-off. Colour and badge.
 *   `earns_nothing` -- the REFUSAL, the exact condition
 *                      App\Services\AreaClosures::assertEarnable refuses a GOT
 *                      IT on. Drives the notice and hides the "Got it!" button.
 *
 * Since gold became conferral-only these carry the same value on a student's
 * payload, and by construction: the only thing that makes an area gold is the
 * closure that also refuses its GOT ITs. They are still read separately, because
 * only one of them may ever REMOVE A CONTROL, and taking a button away has to
 * follow what the server actually does rather than a proxy for it.
 *
 * ## Grey beats gold
 *
 * A section the child may not enter is never dressed up as an invitation to
 * practise, so a locked group and a disabled tile are not badged even if the
 * payload says gold.
 */

/** The badge text. Short, said out loud, and never colour on its own. */
export const GOLD_LABEL = "GE GOLD"

/**
 * Brand gold as a value, for the handful of places a colour is passed as a prop
 * rather than set in a stylesheet (the progress rings and bars). It must stay
 * equal to `$secondary` in styles/colours.scss.
 *
 * Only ever a FILL. Nothing readable is painted in it -- on white it is about
 * 1.3:1. See the note in colours.scss.
 */
export const GOLD_FILL = "#ffd61e"

/**
 * What a child is told BEFORE they start, on the section itself.
 *
 * A gold section opens, the questions are there, and every "Got it!" they tap is
 * refused by the server. Said only in the refusal, an hour of practice scores
 * nothing and the app looks broken.
 *
 * THE OWNER'S WORDS, verbatim, and deliberately short. An earlier draft softened
 * this into an invitation -- "practise as much as you like, just for fun" -- and
 * she replaced it. A gold section is one she closed because it is baby work for
 * that child's level; practising it for credit is cheating, and the line has to
 * say so plainly rather than encourage it. Do not add a clause back.
 *
 * Shown on `earns_nothing`.
 */
export const GOLD_NOTICE = "Mission Accomplished - no GOT ITs can be earned here."

/**
 * What they are told if they tap "Got it!" anyway. The server sends its own two
 * halves (`closed_reason` and `closed_hint`); this is the fallback for a payload
 * without them, worded to match.
 */
export const GOLD_REFUSED_FALLBACK =
	"Your teacher has marked this section finished. You can still practise here, but it will not add to your GOT ITs."

/**
 * Whether a module, lesson group or lesson the server described is gold -- which
 * means, and only means, that the owner has closed it gold.
 *
 * Absent means false: every payload predating this feature, and every section of
 * every student who has had nothing closed. Which is almost all of them, at any
 * percentage.
 */
export const isGold = item => Boolean(item && item.gold)

/**
 * Whether the server will REFUSE a GOT IT in this module, lesson group or lesson
 * -- the owner has closed it gold.
 *
 * This, never `gold`, is what hides the "Got it!" button and shows the notice.
 * They carry the same value today, but only this one is a statement about what
 * the server will do, and only this one may take a control away.
 *
 * Absent means false, which is the safe direction: the control stays, the child
 * can still tap it, and a refusal (if the server disagrees) is handled with a
 * message rather than a vanished button.
 */
export const earnsNothing = item => Boolean(item && item.earns_nothing)

/**
 * "75/100 actual" -- what the student had GENUINELY completed of the questions
 * frozen into a closure, for the owner's eyes only.
 *
 * Her words: "Let's keep their TRUE data visible somewhere in the admin e.g.
 * '75/100 actual' written close by, so I don't have to undo it to be reminded."
 *
 * Returns "" unless the server sent both numbers, so a module that is gold
 * because the student really finished it -- with no closure behind it and so no
 * frozen figures -- says nothing rather than "null/null actual".
 */
export const actualNote = (completed, total) =>
	completed === undefined || completed === null || total === undefined || total === null
		? ""
		: ` (${completed}/${total} actual)`

export default isGold
